import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { academyPath } from '@/lib/schema/paths';

export interface CountryLoginItem {
  icon?: {
    url: string;
    title?: string;
  };
  heading?: string;
  title?: string;
  region_code?: string;
  link?: {
    title?: string;
  };
}

interface PageData {
  country_login?: CountryLoginItem[];
}

// Stable empty fallback — a fresh `[]` per render would give countryLoginItems a new
// identity each time and invalidate the memos that depend on it (here and downstream).
const NO_ITEMS: CountryLoginItem[] = [];

/**
 * Region/SSO button content from the CMS, for the login modal.
 *
 * Uses React Query (not useState/useEffect) because LoginModal unmounts on close, so a
 * hook-local fetch would re-issue an uncached `GET /api/login-data` on every modal open;
 * this is static config, one fetch per session is enough.
 *
 * Path goes through academyPath() rather than a hardcoded string, since that's the single
 * place basePath is derived (lib/schema/paths.ts).
 */
export function useLoginData() {
  const { data, isLoading } = useQuery({
    queryKey: ['login-data'],
    queryFn: async (): Promise<PageData | null> => {
      const response = await fetch(academyPath('/api/login-data'));
      if (!response.ok) return null;
      return (await response.json()) || null;
    },
    staleTime: Infinity,
    retry: false,
  });

  const countryLoginItems = data?.country_login ?? NO_ITEMS;

  const uniqueRegions = useMemo(() => {
    const regions = new Set<string>();
    countryLoginItems.forEach((item) => {
      if (item.title) {
        regions.add(item.title);
      }
    });
    return Array.from(regions).sort();
  }, [countryLoginItems]);

  return {
    countryLoginItems,
    uniqueRegions,
    isLoading,
  };
}
